// Permission levels for organization roles
export const ROLES = {
  OWNER: 'owner',
  ADMIN: 'admin',
  MANAGER: 'manager',
  PILOT: 'pilot',
  VIEWER: 'viewer'
};

// Role hierarchy (higher index = more permissions)
export const ROLE_HIERARCHY = [
  ROLES.VIEWER,
  ROLES.PILOT,
  ROLES.MANAGER,
  ROLES.ADMIN,
  ROLES.OWNER
];

// Feature permissions matrix
export const PERMISSIONS = {
  // Dashboard
  VIEW_DASHBOARD: [ROLES.VIEWER, ROLES.MANAGER, ROLES.ADMIN, ROLES.OWNER],

  // Fleet Management
  VIEW_FLEET: [ROLES.VIEWER, ROLES.PILOT, ROLES.MANAGER, ROLES.ADMIN, ROLES.OWNER],
  MANAGE_DRONES: [ROLES.PILOT, ROLES.MANAGER, ROLES.ADMIN, ROLES.OWNER],
  DELETE_DRONES: [ROLES.MANAGER, ROLES.ADMIN, ROLES.OWNER],
  LOG_FLIGHT_HOURS: [ROLES.PILOT, ROLES.MANAGER, ROLES.ADMIN, ROLES.OWNER],
  EDIT_FLIGHT_LOGS: [ROLES.PILOT, ROLES.MANAGER, ROLES.ADMIN, ROLES.OWNER], // Own logs or manager+
  DELETE_FLIGHT_LOGS: [ROLES.MANAGER, ROLES.ADMIN, ROLES.OWNER],

  // Mission Planning
  VIEW_MISSIONS: [ROLES.VIEWER, ROLES.PILOT, ROLES.MANAGER, ROLES.ADMIN, ROLES.OWNER],
  CREATE_MISSIONS: [ROLES.PILOT, ROLES.MANAGER, ROLES.ADMIN, ROLES.OWNER],
  EDIT_MISSIONS: [ROLES.PILOT, ROLES.MANAGER, ROLES.ADMIN, ROLES.OWNER],
  DELETE_MISSIONS: [ROLES.MANAGER, ROLES.ADMIN, ROLES.OWNER],

  // Flight Operations
  VIEW_OPERATIONS: [ROLES.VIEWER, ROLES.PILOT, ROLES.MANAGER, ROLES.ADMIN, ROLES.OWNER],
  CREATE_OPERATIONS: [ROLES.PILOT, ROLES.MANAGER, ROLES.ADMIN, ROLES.OWNER],
  EDIT_OPERATIONS: [ROLES.PILOT, ROLES.MANAGER, ROLES.ADMIN, ROLES.OWNER],
  DELETE_OPERATIONS: [ROLES.MANAGER, ROLES.ADMIN, ROLES.OWNER],

  // Flight Logs
  LOG_FLIGHT_HOURS: [ROLES.PILOT, ROLES.MANAGER, ROLES.ADMIN, ROLES.OWNER],
  EDIT_FLIGHT_LOGS: [ROLES.PILOT, ROLES.MANAGER, ROLES.ADMIN, ROLES.OWNER],
  DELETE_FLIGHT_LOGS: [ROLES.PILOT, ROLES.MANAGER, ROLES.ADMIN, ROLES.OWNER],

  // LAANC Authorizations
  VIEW_LAANC: [ROLES.VIEWER, ROLES.PILOT, ROLES.MANAGER, ROLES.ADMIN, ROLES.OWNER],
  REQUEST_LAANC: [ROLES.PILOT, ROLES.MANAGER, ROLES.ADMIN, ROLES.OWNER],

  // Compliance
  VIEW_COMPLIANCE: [ROLES.VIEWER, ROLES.PILOT, ROLES.MANAGER, ROLES.ADMIN, ROLES.OWNER],
  VIEW_PILOT_LICENSES: [ROLES.VIEWER, ROLES.MANAGER, ROLES.ADMIN, ROLES.OWNER], // Pilots cannot view licenses
  VIEW_ALL_INCIDENTS: [ROLES.MANAGER, ROLES.ADMIN, ROLES.OWNER], // Pilots can only view their own
  CREATE_INCIDENT: [ROLES.PILOT, ROLES.MANAGER, ROLES.ADMIN, ROLES.OWNER],
  MANAGE_CHECKLIST_TEMPLATES: [ROLES.PILOT, ROLES.MANAGER, ROLES.ADMIN, ROLES.OWNER], // Pilots can create/edit templates
  COMPLETE_CHECKLISTS: [ROLES.PILOT, ROLES.MANAGER, ROLES.ADMIN, ROLES.OWNER], // Pilots can complete checklists
  VIEW_ALL_CHECKLISTS: [ROLES.VIEWER, ROLES.MANAGER, ROLES.ADMIN, ROLES.OWNER], // Viewers can view all completed checklists
  MANAGE_COMPLIANCE: [ROLES.MANAGER, ROLES.ADMIN, ROLES.OWNER],

  // Files
  VIEW_FILES: [ROLES.VIEWER, ROLES.PILOT, ROLES.MANAGER, ROLES.ADMIN, ROLES.OWNER],
  UPLOAD_FILES: [ROLES.PILOT, ROLES.MANAGER, ROLES.ADMIN, ROLES.OWNER],
  DELETE_FILES: [ROLES.MANAGER, ROLES.ADMIN, ROLES.OWNER],

  // Reports & Analytics
  VIEW_REPORTS: [ROLES.MANAGER, ROLES.ADMIN, ROLES.OWNER],
  VIEW_ANALYTICS: [ROLES.MANAGER, ROLES.ADMIN, ROLES.OWNER],
  EXPORT_DATA: [ROLES.MANAGER, ROLES.ADMIN, ROLES.OWNER],

  // Team Management
  VIEW_TEAM: [ROLES.VIEWER, ROLES.PILOT, ROLES.MANAGER, ROLES.ADMIN, ROLES.OWNER],
  INVITE_MEMBERS: [ROLES.MANAGER, ROLES.ADMIN, ROLES.OWNER],
  MANAGE_MEMBERS: [ROLES.ADMIN, ROLES.OWNER],
  REMOVE_MEMBERS: [ROLES.ADMIN, ROLES.OWNER],
  MANAGE_ROLES: [ROLES.ADMIN, ROLES.OWNER],

  // Organization Settings
  VIEW_SETTINGS: [ROLES.ADMIN, ROLES.OWNER],
  MANAGE_ORGANIZATION: [ROLES.ADMIN, ROLES.OWNER],
  EDIT_SETTINGS: [ROLES.ADMIN, ROLES.OWNER],
  DELETE_ORGANIZATION: [ROLES.OWNER],
  MANAGE_INTEGRATIONS: [ROLES.OWNER],

  // Billing
  VIEW_BILLING: [ROLES.ADMIN, ROLES.OWNER],
  MANAGE_BILLING: [ROLES.OWNER],
  CHANGE_PLAN: [ROLES.OWNER],

  // Audit Logs
  VIEW_AUDIT_LOGS: [ROLES.MANAGER, ROLES.ADMIN, ROLES.OWNER]
};

/**
 * Check if a role has a specific permission
 * @param {string} userRole - The user's role in the organization
 * @param {string} permission - The permission to check (from PERMISSIONS object)
 * @returns {boolean} - True if the user has the permission
 */
export const hasPermission = (userRole, permission) => {
  if (!userRole || !permission) return false;

  const allowedRoles = PERMISSIONS[permission];
  if (!allowedRoles) return false;

  return allowedRoles.includes(userRole);
};

/**
 * Check if a role is equal to or higher than a minimum required role
 * @param {string} userRole - The user's role
 * @param {string} minimumRole - The minimum required role
 * @returns {boolean} - True if user's role meets or exceeds the minimum
 */
export const hasMinimumRole = (userRole, minimumRole) => {
  if (!userRole || !minimumRole) return false;

  const userRoleIndex = ROLE_HIERARCHY.indexOf(userRole);
  const minimumRoleIndex = ROLE_HIERARCHY.indexOf(minimumRole);

  if (userRoleIndex === -1 || minimumRoleIndex === -1) return false;

  return userRoleIndex >= minimumRoleIndex;
};

/**
 * Get all permissions for a specific role
 * @param {string} role - The role to get permissions for
 * @returns {Array<string>} - Array of permission names
 */
export const getRolePermissions = (role) => {
  if (!role) return [];

  return Object.keys(PERMISSIONS).filter(permission =>
    PERMISSIONS[permission].includes(role)
  );
};

/**
 * Get display-friendly role name
 * @param {string} role - The role identifier
 * @returns {string} - Formatted role name
 */
export const getRoleDisplayName = (role) => {
  if (!role) return 'Unknown';
  return role.charAt(0).toUpperCase() + role.slice(1);
};

/**
 * Get role description
 * @param {string} role - The role identifier
 * @returns {string} - Role description
 */
export const getRoleDescription = (role) => {
  const descriptions = {
    [ROLES.OWNER]: 'Full access to all features including billing and team management',
    [ROLES.ADMIN]: 'Manage team members, settings, and all operational features',
    [ROLES.MANAGER]: 'Manage operations, compliance, and view analytics',
    [ROLES.PILOT]: 'Create and manage missions, drones, and flight operations',
    [ROLES.VIEWER]: 'View-only access to organization data'
  };

  return descriptions[role] || 'No description available';
};

export default {
  ROLES,
  ROLE_HIERARCHY,
  PERMISSIONS,
  hasPermission,
  hasMinimumRole,
  getRolePermissions,
  getRoleDisplayName,
  getRoleDescription
};
